import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { Price } from "@/Apollo/schema";
import { IconProfileProBuyer } from "@/assets/icons/figma/IconProfileProBuyer";
import { formatAccountMoney } from "@/common/utils/account-format.utils";
import { InfoLabel } from "@/ui/cva/InfoLabel";

/**
 * Small Square Light Brand label on a live ad in My Ads: "Pro Buyer -15%".
 * Rendered by AdsListRow only when the gateway returned a preview price for
 * the ad; the row decides, this component only draws.
 */
export const TradeDiscountAdLabel = ({ pct }: { pct: number }) => {
  const { t } = useTranslation();

  return (
    <InfoLabel
      color="brand"
      variant="light"
      size="small"
      corners="square"
      className="min-w-0 max-w-fit whitespace-nowrap"
      data-testid="pro-buyer-discount-label"
    >
      {t("account-ads:pro-buyer-discount-ad-label", {
        defaultValue: "Pro Buyer -{{pct}}%",
        pct,
      })}
    </InfoLabel>
  );
};

type TradeDiscountAdPriceProps = {
  /** `Product.tradeDiscountPreviewPrice`: owner-only, computed by the gateway. */
  price: Pick<Price, "amount" | "currency"> | null | undefined;
};

/**
 * Second line of the My Ads price column: the Pro Buyer icon and "EUR 6.000,- for
 * Pro Buyers" in brand green. The amount is the gateway's own preview price,
 * so eligibility (curated, live auction, buy-now or asking price) is decided
 * there alone. Null renders nothing.
 */
export const TradeDiscountAdPrice = ({ price }: TradeDiscountAdPriceProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  if (!price) {
    return null;
  }

  const formatted = formatAccountMoney(price.amount, {
    locale,
    currency: price.currency,
  });

  return (
    <span
      className="flex items-center justify-start gap-1 whitespace-nowrap font-body text-sm font-normal text-brand-600 lg:justify-end"
      data-testid="pro-buyer-discount-price"
    >
      <IconProfileProBuyer className="size-4 shrink-0" />
      {t("account-ads:pro-buyer-row-price", {
        defaultValue: "{{price}} for Pro Buyers",
        price: formatted,
      })}
    </span>
  );
};
